import {
  LifeReadinessCategory,
  LifeReadinessQuestion,
  LifeReadinessSubcategory,
  QuestionRowProps,
} from '@backrs_ui/common-ui';

import { QuestionnaireProgressTabProps } from '@backrs_ui/common-ui/src/atoms/QuestionnaireProgressTab';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

const QUESTIONNAIRE_STRINGS = appStrings.modules.lifeReadiness.questionTooltips;

export const LEVEL_MAP: Record<string, 1 | 2 | 3 | 4> = {
  level_one: 1,
  level_two: 2,
  level_three: 3,
  level_four: 4,
};

/**
 * Returns the tooltip for a subcategory.
 */
export const getTooltips = (subcategoryTitle?: string): { title: string; tooltip: string }[] => {
  if (subcategoryTitle?.toLowerCase() === 'knowledge') {
    return Object.values(QUESTIONNAIRE_STRINGS.knowledge);
  }

  return Object.values(QUESTIONNAIRE_STRINGS.achievement);
};

/**
 * Formats question answers for QuestionRowProps with selected levels.
 */
const getAnswers = (
  question: LifeReadinessQuestion,
  userLevelMap: Record<string, number>,
): QuestionRowProps['answers'] => {
  return [
    {
      key: 'level_one',
      value: question.level_one,
      isSelected: userLevelMap[question.id] === 1,
    },
    {
      key: 'level_two',
      value: question.level_two,
      isSelected: userLevelMap[question.id] === 2,
    },
    {
      key: 'level_three',
      value: question.level_three,
      isSelected: userLevelMap[question.id] === 3,
    },
    {
      key: 'level_four',
      value: question.level_four,
      isSelected: userLevelMap[question.id] === 4,
    },
  ];
};

/**
 *  Builds the tabs props.
 *  1. Determines whether the category has been fully answered, thus should be highlighted.
 *  2. Determines whether the category is the current active category, thus should be marked active.
 */
export const getProgressTabsProps = (
  categories: LifeReadinessCategory[],
  subcategories: LifeReadinessSubcategory[],
  questions: LifeReadinessQuestion[],
  userLevelMap: Record<string, number>,
  currentCategory: LifeReadinessCategory,
): QuestionnaireProgressTabProps[] => {
  return categories.map((category) => {
    // find all subcategories that belong to the current category
    const categorySubcategories = subcategories.filter(
      (s) => s.life_survey_category_id === category.id,
    );

    // find all questions belonging to the current category
    const categoryQuestions = questions.filter((q) =>
      categorySubcategories.some((s) => s.id === q.life_survey_subcategory_id),
    );

    // determine if all questions in the category have been answered
    const allQuestionsAnswered =
      categoryQuestions.length > 0 &&
      categoryQuestions.every((q) => userLevelMap[q.id] !== undefined);

    const isCategoryActive = category.slug === currentCategory.slug;

    return {
      title: category.name,
      subcategories: categorySubcategories.map((subcat) => ({
        id: subcat.name.toLowerCase(),
        label: subcat.name,
      })),
      highlighted: allQuestionsAnswered,
      active: isCategoryActive,
    };
  });
};

/**
 * Get the questions for a subcategory, and transform to the format expected by the QuestionView component.
 */
export const getQuestionsForSubcategory = (
  questions: LifeReadinessQuestion[],
  subcategoryId: number,
  userLevelMap: Record<string, number>,
  handleAnswerChange: (question: LifeReadinessQuestion, key: string) => void,
): Omit<QuestionRowProps, 'toggleExpand' | 'expanded'>[] => {
  return questions
    .filter((q) => q.life_survey_subcategory_id === subcategoryId)
    .map((q) => ({
      title: q.name,
      description: q.description,
      onAnswerChange: (key: string) => handleAnswerChange(q, key),
      answers: getAnswers(q, userLevelMap),
    }));
};
